import { Controller, useForm } from "react-hook-form";
import { Flex } from "../../../../components/Flex";
import { Modal } from "../../../../components/modal/Modal";
import { useTranslation } from "react-i18next";
import { MetaCreateConversationDto } from "../../../../model/integration/dto/meta/MetaCreateConversationDto";
import { Button } from "../../../../components/Button";
import { Input } from "../../../../components/Input";
import { Info } from "../../../../components/Info";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { useState } from "react";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  setActiveChatId: (chatId: string) => void;
};

export const WACreateChatModal: React.FC<Props> = ({
  isOpen,
  onClose,
  setActiveChatId,
}) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const whatsAppMetaIntegration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );

  const { control, getValues, handleSubmit } =
    useForm<MetaCreateConversationDto>();

  const onCreate = async () => {
    try {
      setLoading(true);
      const dto = {
        ...getValues(),
        userId,
        integrationId: whatsAppMetaIntegration?._id,
      } as MetaCreateConversationDto;

      const chat = await dispatch(
        integrationActions.createWhatsappMetaConversation(dto),
      ).unwrap();

      if (chat) {
        // Writing to someone who is already in the inbox hands back their
        // existing chat, and that chat can be too old to be in the list this
        // route fetched. Putting it in is a no-op when it is already there.
        dispatch(integrationActions.metaInsertConversation(chat));
        setActiveChatId(chat._id);
      }

      onClose();
    } catch (e) {
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={t("integration.whatsapp.createChat.modal.title")}
    >
      <Flex flexDirection="column" gap="l">
        <Info info={t("integration.whatsapp.createChat.modal.description")} />
        <Flex flexDirection="column" gap="m">
          <Controller
            control={control}
            name="waId"
            rules={{ required: true }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                onChange={onChange}
                value={value}
                title={t(
                  "integration.whatsapp.createChat.modal.input.phone.title",
                )}
                placeholder={t(
                  "integration.whatsapp.createChat.modal.input.phone.placeholder",
                )}
                required
                type="tel"
                valid={!error}
              />
            )}
          />
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, value } }) => (
              <Input
                onChange={onChange}
                value={value}
                title={t(
                  "integration.whatsapp.createChat.modal.input.name.title",
                )}
                placeholder={t(
                  "integration.whatsapp.createChat.modal.input.name.placeholder",
                )}
                maxLength={Lengths.DESCRIPTION}
              />
            )}
          />
          <Button onClick={handleSubmit(onCreate)} loading={loading}>
            {t("integration.whatsapp.createChat.modal.button")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
